import {
  PartnerStoreSettingsType,
  StoreListing,
  StoreListingPrice,
  StoreListingPricePeriod,
} from '@portals/api/partners';
import { CurrencyCode, PeriodEnum, ProductPricingModel } from '@portals/types';

import {
  PaymentSettingItemPriceType,
  PaymentSettingsType,
} from '../types/store-listings.types';

interface MapStoreListingPricesToPaymentSettingsParams {
  defaultCurrency: PartnerStoreSettingsType['default_currency'] | undefined;
  currencies: PartnerStoreSettingsType['currencies'] | undefined;
  storeListingPrices: StoreListing['prices'] | undefined;
}
export function mapStoreListingPricesToPaymentSettings({
  defaultCurrency,
  currencies,
  storeListingPrices,
}: MapStoreListingPricesToPaymentSettingsParams) {
  if (!defaultCurrency || !currencies || !storeListingPrices) {
    return {};
  }

  const paymentSettings: Partial<PaymentSettingsType> = {};

  const oneTimePrices = getPrices({
    pricePeriod: 'one_time_price_in_scu',
    prices: storeListingPrices,
    currencies,
    defaultCurrency,
  });
  const monthlyPrices = getPrices({
    pricePeriod: 'monthly_price_in_scu',
    prices: storeListingPrices,
    currencies,
    defaultCurrency,
  });
  const yearlyPrices = getPrices({
    pricePeriod: 'yearly_price_in_scu',
    prices: storeListingPrices,
    currencies,
    defaultCurrency,
  });
  const usageBasedPrices = getPrices({
    pricePeriod: 'usage_based_price_in_scu',
    prices: storeListingPrices,
    currencies,
    defaultCurrency,
  });

  if (oneTimePrices) {
    paymentSettings[PeriodEnum.OneTime] = {
      period: PeriodEnum.OneTime,
      prices: oneTimePrices,
    };
  }
  if (monthlyPrices) {
    paymentSettings[PeriodEnum.Monthly] = {
      period: PeriodEnum.Monthly,
      prices: monthlyPrices,
    };
  }
  if (yearlyPrices) {
    paymentSettings[PeriodEnum.Yearly] = {
      period: PeriodEnum.Yearly,
      prices: yearlyPrices,
    };
  }
  if (usageBasedPrices) {
    paymentSettings[ProductPricingModel.UsageBased] = {
      period: ProductPricingModel.UsageBased,
      prices: usageBasedPrices,
    };
  }

  return paymentSettings;
}

interface GetPricesParams {
  pricePeriod: StoreListingPricePeriod;
  prices: StoreListing['prices'];
  currencies: PartnerStoreSettingsType['currencies'];
  defaultCurrency: PartnerStoreSettingsType['default_currency'];
}
function getPrices({
  pricePeriod,
  prices,
  defaultCurrency,
  currencies,
}: GetPricesParams): Array<PaymentSettingItemPriceType> | undefined {
  const currencyToValueMap: Partial<
    Record<CurrencyCode, PaymentSettingItemPriceType['amountInScu']>
  > = {};

  prices.forEach((price) => {
    currencyToValueMap[price.currency] = price[pricePeriod];
  });

  if (Object.values(currencyToValueMap).every((value) => value === null)) {
    return undefined;
  }

  return currencies
    .map((currency) => ({
      currency,
      error: false,
      amountInScu: currencyToValueMap[currency] ?? null,
    }))
    .sort((a) => (a.currency === defaultCurrency ? -1 : 1));
}

interface MapPaymentSettingsToStoreListingPricesParams {
  pricingModel: StoreListing['product']['pricing_model'];
  currencies: PartnerStoreSettingsType['currencies'];
  paymentSettings: Partial<PaymentSettingsType>;
}
export function mapPaymentSettingsToStoreListingPrices({
  pricingModel,
  currencies,
  paymentSettings,
}: MapPaymentSettingsToStoreListingPricesParams) {
  if (pricingModel === ProductPricingModel.Personalized) {
    return [];
  }

  const prices: StoreListingPrice[] = currencies.map((currency) => ({
    currency,
    one_time_price_in_scu:
      paymentSettings[PeriodEnum.OneTime]?.prices.find(
        (price) => price.currency === currency
      )?.amountInScu ?? null,

    monthly_price_in_scu:
      paymentSettings[PeriodEnum.Monthly]?.prices.find(
        (price) => price.currency === currency
      )?.amountInScu ?? null,

    yearly_price_in_scu:
      paymentSettings[PeriodEnum.Yearly]?.prices.find(
        (price) => price.currency === currency
      )?.amountInScu ?? null,

    usage_based_price_in_scu:
      paymentSettings[ProductPricingModel.UsageBased]?.prices.find(
        (price) => price.currency === currency
      )?.amountInScu ?? null,
  }));

  return prices;
}
